import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FormFieldHidden from "ds/components/Form/Field/Hidden";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { BitbucketDatacenterIntegration, VcsProvider } from "types/generated";
import { stringIsRequired, validateRequiredURL } from "utils/formValidators";
import { AccountContext } from "views/AccountWrapper";

import VCSIntegrationTypeFieldControl from "../../components/IntegrationTypeFieldControl";
import VCSIntegrationsDocumentationButton from "../components/DocumentationButton";
import VCSChecksToggle from "../components/VCSChecksToggle";
import useCloseFullScreenModal from "../useCloseFullScreenModal";
import {
  getFormDefaultValues,
  mapCreateIntegrationInputValues,
  mapUpdateIntegrationInputValues,
} from "./helpers";
import { ManageBitbucketDataCenterFormFields } from "./types";
import useCheckBitbucketDataCenterHasDefaultIntegration from "./useCheckBitbucketDataCenterHasDefaultIntegration";
import useCreateBitbucketDataCenterIntegration from "./useCreateBitbucketDataCenterIntegration";
import useUpdateBitbucketDataCenterIntegration from "./useUpdateBitbucketDataCenterIntegration";
import GitCheckoutToggle from "../components/GitCheckoutToggle";

type VCSIntegrationsManageBitbucketDataCenterFormProps = {
  integration?: BitbucketDatacenterIntegration;
  onIsDirtyChange: (value: boolean) => void;
};

const VCSIntegrationsManageBitbucketDataCenterForm = ({
  integration,
  onIsDirtyChange,
}: VCSIntegrationsManageBitbucketDataCenterFormProps) => {
  const isEditMode = !!integration;
  const { viewer } = useTypedContext(AccountContext);

  const { handleOnClose } = useCloseFullScreenModal();

  const manageBitbucketDataCenterForm = useForm<ManageBitbucketDataCenterFormFields>({
    defaultValues: getFormDefaultValues(integration),
    mode: "onChange",
  });

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    setValue,
    trigger,
  } = manageBitbucketDataCenterForm;

  const { createBitbucketDataCenterIntegration, loading: createLoading } =
    useCreateBitbucketDataCenterIntegration();
  const { updateBitbucketDataCenterIntegration, loading: updateLoading } =
    useUpdateBitbucketDataCenterIntegration();

  const { hasDefaultIntegration, loading: checkDefaultIntegrationLoading } =
    useCheckBitbucketDataCenterHasDefaultIntegration(isEditMode);

  const withIntegrationTypeControl = !isEditMode && viewer.admin;
  const withSpaceControl = !withIntegrationTypeControl && !integration?.isDefault;

  const onHiddenToggle = useCallback(
    (isHidden: boolean) => {
      if (!isHidden) {
        setValue("accessToken", "", { shouldValidate: true });
      } else {
        // revalidate the whole form with hidden field
        trigger();
      }
    },
    [setValue, trigger]
  );

  const handleSetup = useCallback(
    () =>
      handleSubmit((formData: ManageBitbucketDataCenterFormFields) => {
        if (isEditMode) {
          updateBitbucketDataCenterIntegration(
            mapUpdateIntegrationInputValues(formData, integration.id),
            handleOnClose
          );
        } else {
          createBitbucketDataCenterIntegration(
            mapCreateIntegrationInputValues(formData),
            handleOnClose
          );
        }
      })(),
    [
      createBitbucketDataCenterIntegration,
      handleOnClose,
      integration,
      isEditMode,
      updateBitbucketDataCenterIntegration,
      handleSubmit,
    ]
  );

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  return (
    <FormProvider {...manageBitbucketDataCenterForm}>
      <FullScreenModalBody>
        <FormField
          label="Integration name"
          helperText="The integration name is unique and cannot be changed"
          error={errors?.name?.message}
          noMargin
        >
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter integration name here"
              error={!!errors?.name}
              {...register("name", {
                validate: stringIsRequired("Integration name field is required."),
              })}
              disabled={isEditMode}
              {...ariaInputProps}
            />
          )}
        </FormField>

        {withIntegrationTypeControl && (
          <VCSIntegrationTypeFieldControl
            hasAlreadyDefaultIntegration={hasDefaultIntegration}
            loading={checkDefaultIntegrationLoading}
            provider={VcsProvider.BitbucketDatacenter}
          />
        )}
        {withSpaceControl && <FormFieldSpace />}

        <FormField label="API host URL" error={errors?.apiHost?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="https://"
              error={!!errors?.apiHost}
              {...register("apiHost", {
                validate: validateRequiredURL(),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        <FormField label="User facing host URL" error={errors?.userFacingHost?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="https://"
              error={!!errors?.userFacingHost}
              {...register("userFacingHost", { validate: validateRequiredURL() })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        <FormField label="Username" error={errors?.username?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter username here"
              error={!!errors?.username}
              {...register("username", {
                validate: stringIsRequired("Username field is required."),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        {!isEditMode && (
          <FormField label="Access token" error={errors?.accessToken?.message}>
            {({ ariaInputProps }) => (
              <SecretInput
                placeholder="Enter access token here"
                error={!!errors?.accessToken}
                {...register("accessToken", {
                  validate: stringIsRequired("Access token field is required."),
                })}
                {...ariaInputProps}
              />
            )}
          </FormField>
        )}

        {isEditMode && (
          <FormFieldHidden
            hiddenPlaceholder="Update access token"
            onHiddenToggle={onHiddenToggle}
            label="Access token"
            error={errors?.accessToken?.message}
          >
            {(isHidden) => (
              <SecretInput
                placeholder="Enter access token here"
                error={!!errors?.accessToken}
                {...register("accessToken", {
                  ...(!isHidden && {
                    validate: stringIsRequired("Access token field is required."),
                  }),
                })}
              />
            )}
          </FormFieldHidden>
        )}

        <GitCheckoutToggle />
        <VCSChecksToggle />

        <FormFieldTags label="Labels" tagName="label" name="labels" isOptional />

        <FormField label="Description" isOptional>
          {({ ariaInputProps }) => (
            <Textarea
              placeholder="Enter description here"
              {...register("description")}
              {...ariaInputProps}
            />
          )}
        </FormField>
      </FullScreenModalBody>
      <FullScreenModalFooter justify="between">
        <VCSIntegrationsDocumentationButton provider={VcsProvider.BitbucketDatacenter} />

        <Button onPress={handleSetup} variant="primary" loading={createLoading || updateLoading}>
          {isEditMode ? "Save" : "Set up"}
        </Button>
      </FullScreenModalFooter>
    </FormProvider>
  );
};

export default VCSIntegrationsManageBitbucketDataCenterForm;
